<template>
    <div class="ui-page">
        <header class="ui-page-header">
            <router-link :to="listRoute" class="ui-eyebrow"><v-icon small>mdi-arrow-left</v-icon> Ativos</router-link>
            <div class="ui-page-header__row">
                <div class="ui-page-header__text">
                    <div v-if="asset" class="cp-card__slug">{{ asset.code }}</div>
                    <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                        <h1 class="ui-title">{{ isNew ? "Novo ativo" : asset ? asset.title : "Carregando..." }}</h1>
                        <ui-badge v-if="asset" set="cp-asset" :status="asset.status" />
                    </div>
                    <div v-if="asset && (asset.groups.length || asset.stages.length)" class="d-flex align-center flex-wrap mt-1" style="gap: 6px;">
                        <ui-badge v-for="group in asset.groups" :key="group.id" :text="`${group.kind_label} ${group.code}`" tone="primary" :dot="false" />
                        <ui-badge v-for="stage in asset.stages" :key="`s${stage.id}`" :text="`Etapa ${stage.number}`" tone="purple" :dot="false" />
                    </div>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn v-if="asset && $allowed('delete')" outlined class="ui-btn-danger" @click="remove">Excluir</v-btn>
                    <v-btn v-if="$allowed(isNew ? 'create' : 'edit')" color="primary" depressed :loading="saving" @click="save">
                        <v-icon left small>mdi-content-save-outline</v-icon>{{ isNew ? "Criar ativo" : "Salvar alterações" }}
                    </v-btn>
                </div>
            </div>
        </header>

        <div v-if="loading" class="ui-empty"><v-progress-circular indeterminate size="28" color="primary"></v-progress-circular></div>
        <ui-empty v-else-if="isNew && !processId" icon="mdi-domain" text="Processo não informado. Abra o cadastro pela lista de ativos de um processo." />
        <v-row v-else>
            <v-col cols="12" md="7">
                <ui-section title="Identificação" description="Como o ativo aparece na vitrine, no edital e nos documentos.">
                    <v-row dense>
                        <v-col cols="12" sm="4">
                            <v-text-field v-model="form.code" label="Código" outlined dense hide-details="auto" maxlength="30" :placeholder="isNew ? 'Automático' : ''"
                                persistent-placeholder hint="Em branco, a plataforma gera (A-001, A-002…)." :persistent-hint="isNew"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="8"><v-select v-model="form.asset_type" :items="options.asset_types" label="Tipo" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12"><v-text-field v-model="form.title" label="Descrição curta *" outlined dense hide-details="auto" :error-messages="showErrors && !String(form.title || '').trim() ? ['Obrigatório'] : []" maxlength="255" counter="255" placeholder="Ex.: Unidade de recebimento de grãos de Cruz Alta"></v-text-field></v-col>
                        <v-col cols="12"><v-textarea v-model="form.description" label="Descrição completa" outlined dense rows="4" auto-grow hide-details="auto"></v-textarea></v-col>
                        <v-col cols="12" sm="5"><v-text-field v-model="form.registration" label="Matrícula" outlined dense hide-details="auto" maxlength="120" placeholder="Ex.: 12.345"></v-text-field></v-col>
                        <v-col cols="12" sm="7"><v-text-field v-model="form.registry_office" label="Cartório de registro" outlined dense hide-details="auto" maxlength="255" placeholder="Ex.: Registro de Imóveis de Cruz Alta/RS"></v-text-field></v-col>
                    </v-row>
                </ui-section>

                <ui-section title="Localização" description="Busque o endereço ou o nome do local para preencher os campos e as coordenadas. Ajuste o ponto no mapa se precisar." class="mt-4">
                    <v-text-field ref="placeSearch" v-model="placeQuery" label="Buscar endereço ou local" outlined dense hide-details="auto" prepend-inner-icon="mdi-magnify" clearable
                        autocomplete="off" :disabled="!mapsReady" :hint="mapsReady ? '' : 'Carregando a busca do Google Maps…'" persistent-hint class="mb-4" @keydown.enter.prevent></v-text-field>
                    <v-row dense>
                        <v-col cols="12" sm="4">
                            <v-text-field v-model="form.zip_code" v-mask="'#####-###'" label="CEP" outlined dense hide-details="auto" placeholder="00000-000" :loading="zipLoading" @blur="lookupZip"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="8"><v-text-field v-model="form.address" label="Endereço" outlined dense hide-details="auto" maxlength="255" placeholder="Rua, número, bairro ou estrada, km"></v-text-field></v-col>
                        <v-col cols="8"><v-text-field v-model="form.city" label="Município" outlined dense hide-details="auto" maxlength="120"></v-text-field></v-col>
                        <v-col cols="4"><v-autocomplete v-model="form.state" :items="options.states" label="UF" clearable outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-autocomplete></v-col>
                        <v-col cols="6"><v-text-field v-model="form.latitude" label="Latitude" outlined dense hide-details="auto" placeholder="-28,6383000" inputmode="decimal" :error-messages="coordinateError('latitude', 90)"></v-text-field></v-col>
                        <v-col cols="6"><v-text-field v-model="form.longitude" label="Longitude" outlined dense hide-details="auto" placeholder="-53,6064000" inputmode="decimal" :error-messages="coordinateError('longitude', 180)"></v-text-field></v-col>
                    </v-row>
                    <div class="cp-asset-map mt-3">
                        <GmapMap v-if="mapsReady" :center="mapCenter" :zoom="mapZoom" map-type-id="hybrid" style="width: 100%; height: 100%;"
                            :options="{ streetViewControl: false, fullscreenControl: true, mapTypeControl: true, clickableIcons: false }" @click="onMapClick">
                            <GmapMarker v-if="hasCoordinates" :position="markerPosition" :draggable="true" @dragend="onMapClick" />
                        </GmapMap>
                    </div>
                    <div class="d-flex align-center flex-wrap mt-2" style="gap: 12px;">
                        <span class="ui-small ui-muted">{{ hasCoordinates ? "Arraste o marcador ou clique no mapa para corrigir o ponto." : "Clique no mapa para marcar o ponto." }}</span>
                        <v-spacer></v-spacer>
                        <a v-if="mapUrl" :href="mapUrl" target="_blank" rel="noopener" class="ui-small d-inline-flex align-center"><v-icon x-small class="mr-1">mdi-open-in-new</v-icon>Abrir no Google Maps</a>
                        <v-btn v-if="hasCoordinates" x-small text @click="clearCoordinates">Limpar coordenadas</v-btn>
                    </div>
                </ui-section>

                <ui-section title="Área e avaliação" class="mt-4">
                    <v-row dense>
                        <v-col cols="7" sm="4"><v-text-field v-model="form.area" label="Área" outlined dense hide-details="auto" placeholder="0,00" inputmode="numeric" class="cp-input-num"></v-text-field></v-col>
                        <v-col cols="5" sm="3"><v-select v-model="form.area_unit" :items="options.area_units" label="Unidade" clearable outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="5"></v-col>
                        <v-col cols="12" sm="7"><v-text-field v-model="form.appraisal_value" label="Valor de avaliação" prefix="R$" outlined dense hide-details="auto" placeholder="0,00" inputmode="numeric" class="cp-input-num"></v-text-field></v-col>
                        <v-col cols="12" sm="5"><v-text-field v-model="form.appraisal_date" type="date" label="Data do laudo" outlined dense hide-details="auto" :max="today"></v-text-field></v-col>
                    </v-row>
                </ui-section>

                <ui-section title="Situação" description="Ativo bloqueado ou retirado não pode entrar em etapa nova." class="mt-4">
                    <v-row dense>
                        <v-col cols="12" sm="5"><v-select v-model="form.status" :items="options.asset_statuses" label="Situação" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="7"><v-text-field v-model="form.block_reason" :disabled="form.status === 'available'" :label="form.status === 'available' ? 'Motivo (bloqueio ou retirada)' : 'Motivo *'" outlined dense hide-details="auto" :error-messages="showErrors && form.status !== 'available' && !String(form.block_reason || '').trim() ? ['Obrigatório para bloqueio ou retirada'] : []" maxlength="500"></v-text-field></v-col>
                        <v-col cols="12"><v-textarea v-model="form.notes" label="Anotação interna (fora da vitrine)" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea></v-col>
                    </v-row>
                </ui-section>
            </v-col>

            <v-col cols="12" md="5">
                <ui-section title="Laudo de avaliação" description="PDF de até 200 MB. O SHA-256 fica registrado.">
                    <p v-if="!asset" class="ui-small ui-muted mb-0">Disponível depois de criar o ativo.</p>
                    <template v-else>
                        <div v-if="asset.has_appraisal_file && !appraisal.file" class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
                            <v-icon small>mdi-file-pdf-box</v-icon>
                            <div class="flex-grow-1" style="min-width: 0;">
                                <div class="ui-cell-main">{{ asset.appraisal_file_name || "Laudo" }}</div>
                                <div class="ui-cell-sub ui-mono" :title="asset.appraisal_file_hash">SHA-256 {{ asset.appraisal_file_hash ? `${asset.appraisal_file_hash.slice(0, 16)}…` : "—" }}</div>
                            </div>
                            <v-btn small outlined @click="downloadAppraisal"><v-icon left small>mdi-download</v-icon>Baixar</v-btn>
                            <v-btn v-if="$allowed('save')" small outlined class="ui-btn-danger" @click="removeAppraisal">Remover</v-btn>
                        </div>
                        <ui-file-drop :file="appraisal.file" accept="application/pdf" :max-size-mb="200" hint="PDF de até 200 MB" :loading="appraisal.saving"
                            @select="appraisal.file = $event" @save="saveAppraisal" @error="warn" />
                        <v-progress-linear v-if="appraisal.saving" :value="appraisal.progress" height="4" rounded color="primary" class="mt-2"></v-progress-linear>
                    </template>
                </ui-section>

                <ui-section title="Fotos" :description="asset ? `${media.length} ${media.length === 1 ? 'foto' : 'fotos'} · a capa aparece na vitrine` : 'JPG, PNG ou WEBP de até 20 MB.'" class="mt-4">
                    <template v-if="asset && $allowed('save')" #actions>
                        <v-btn small outlined :loading="mediaUpload.busy" @click="$refs.media.click()"><v-icon left small>mdi-image-plus</v-icon>Adicionar fotos</v-btn>
                        <input ref="media" type="file" accept="image/png,image/jpeg,image/webp" multiple hidden @change="addMedia">
                    </template>
                    <p v-if="!asset" class="ui-small ui-muted mb-0">Disponível depois de criar o ativo.</p>
                    <template v-else>
                        <div v-if="mediaUpload.busy" class="ui-small ui-muted mb-2">Enviando {{ mediaUpload.done + 1 }} de {{ mediaUpload.total }}…</div>
                        <div v-if="media.length" class="cp-media">
                            <div v-for="item in media" :key="item.id" class="cp-media__item">
                                <a :href="item.url" target="_blank" rel="noopener"><img :src="item.url" :alt="item.caption || item.file_name" class="cp-media__img"></a>
                                <div class="cp-media__meta">
                                    <ui-badge v-if="item.is_cover" text="Capa" tone="success" :dot="false" />
                                    <v-btn v-else-if="$allowed('save')" x-small text @click="setCover(item)">Usar como capa</v-btn>
                                    <v-btn v-if="$allowed('save')" x-small icon aria-label="Excluir foto" @click="removeMedia(item)"><v-icon x-small>mdi-delete-outline</v-icon></v-btn>
                                </div>
                            </div>
                        </div>
                        <p v-else class="ui-small ui-muted mb-0">Nenhuma foto.</p>
                    </template>
                </ui-section>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import axios from "axios";
import { API, loadOptions, maskCoordinate, maskDecimal, moneyInput, notifyError, notifySuccess, openUrl, uploadForRecord } from "@/components/competitive/competitive.util";

const EMPTY = {
    code: "", asset_type: "other", title: "", description: "", registration: "", registry_office: "", zip_code: "", address: "", city: "", state: null,
    latitude: "", longitude: "", area: "", area_unit: null, appraisal_value: "", appraisal_date: "", status: "available", block_reason: "", notes: "",
};
const BRAZIL = { lat: -14.235, lng: -51.9253 };
const coordinateText = (value) => (value === null || value === undefined || value === "" ? "" : Number(value).toFixed(7).replace(".", ","));
const coordinateNumber = (value) => {
    const text = String(value || "").trim().replace(",", ".");
    return text && /^-?\d{1,3}(\.\d+)?$/.test(text) ? Number(text) : null;
};

// Pecas de um resultado do Google (Places ou Geocoder) nos campos do ativo.
function addressParts(result) {
    const part = (type, short = false) => {
        const found = (result.address_components || []).find((component) => component.types.includes(type));
        return found ? (short ? found.short_name : found.long_name) : "";
    };
    const street = [part("route"), part("street_number")].filter(Boolean).join(", ");
    const district = part("sublocality_level_1") || part("sublocality");
    const city = part("administrative_area_level_2") || part("locality");
    let address = [street, district].filter(Boolean).join(" - ");
    if (!street && result.name && result.name !== city) address = [result.name, district].filter(Boolean).join(" - ");
    const location = result.geometry && result.geometry.location;
    return {
        address,
        city,
        state: part("administrative_area_level_1", true) || null,
        zip_code: part("postal_code"),
        latitude: location ? location.lat() : null,
        longitude: location ? location.lng() : null,
    };
}

// Cadastro do ativo em tela propria: dados, localizacao com busca do Google e
// mapa, avaliacao, laudo (PDF com SHA-256) e fotos.
export default {
    name: "CompetitiveAssetDetails",
    data: () => ({
        asset: null,
        loading: false,
        saving: false,
        options: { asset_statuses: [], asset_types: [], states: [], area_units: [] },
        form: { ...EMPTY },
        media: [],
        appraisal: { file: null, saving: false, progress: 0 },
        mediaUpload: { busy: false, done: 0, total: 0 },
        mapsReady: false,
        placeQuery: "",
        autocomplete: null,
        zipLoading: false,
        showErrors: false,
        today: new Date().toISOString().slice(0, 10),
    }),
    computed: {
        id() {
            return this.$route.params.id;
        },
        isNew() {
            return ["novo", "new"].includes(this.id);
        },
        processId() {
            if (this.asset) return this.asset.process_id;
            return Number(this.$route.query.processo) || null;
        },
        listRoute() {
            return { path: "/processo-competitivo/ativos", query: this.processId ? { processo: String(this.processId) } : {} };
        },
        // O que impede salvar; o botao fica ativo e o clique avisa o que falta.
        missing() {
            const list = [];
            if (!String(this.form.title || "").trim()) list.push("descrição curta");
            if (this.isNew && !this.processId) list.push("processo");
            if (this.form.status !== "available" && !String(this.form.block_reason || "").trim()) list.push("motivo do bloqueio ou da retirada");
            if (this.coordinateError("latitude", 90).length || this.coordinateError("longitude", 180).length) list.push("latitude e longitude válidas");
            return list;
        },
        markerPosition() {
            return { lat: coordinateNumber(this.form.latitude), lng: coordinateNumber(this.form.longitude) };
        },
        hasCoordinates() {
            return this.markerPosition.lat !== null && this.markerPosition.lng !== null;
        },
        mapCenter() {
            return this.hasCoordinates ? this.markerPosition : BRAZIL;
        },
        mapZoom() {
            return this.hasCoordinates ? 16 : 4;
        },
        mapUrl() {
            return this.hasCoordinates ? `https://www.google.com/maps/search/?api=1&query=${this.markerPosition.lat},${this.markerPosition.lng}` : null;
        },
    },
    watch: {
        id() {
            this.load();
        },
        "form.appraisal_value"(value) {
            this.applyMask("appraisal_value", maskDecimal(value));
        },
        "form.area"(value) {
            this.applyMask("area", maskDecimal(value));
        },
        "form.latitude"(value) {
            this.applyMask("latitude", maskCoordinate(value, 2));
        },
        "form.longitude"(value) {
            this.applyMask("longitude", maskCoordinate(value, 3));
        },
        loading(busy) {
            if (!busy) this.$nextTick(this.bindAutocomplete);
        },
    },
    async created() {
        try {
            this.options = await loadOptions();
        } catch (error) {
            notifyError(this, error);
        }
        this.load();
    },
    async mounted() {
        try {
            await this.$gmapApiPromiseLazy();
            this.mapsReady = true;
            this.$nextTick(this.bindAutocomplete);
        } catch (error) {
            this.warn("Não foi possível carregar o Google Maps. Preencha o endereço e as coordenadas à mão.");
        }
    },
    beforeDestroy() {
        if (this.autocomplete && window.google) window.google.maps.event.clearInstanceListeners(this.autocomplete);
    },
    methods: {
        warn(message) {
            this.$dialog.notify.warning(message, { position: "top-right", timeout: 4000 });
        },
        applyMask(key, masked) {
            if (masked !== this.form[key]) this.form[key] = masked;
        },
        coordinateError(key, limit) {
            const text = String(this.form[key] || "").trim();
            if (!text || text === "-") return [];
            const value = coordinateNumber(text);
            if (value === null || Math.abs(value) > limit) return [`Entre -${limit} e ${limit}`];
            const other = key === "latitude" ? this.form.longitude : this.form.latitude;
            return String(other || "").trim() ? [] : ["Informe latitude e longitude juntas"];
        },
        // A busca usa o Places do Google (ja carregado no back-office), restrita ao Brasil.
        bindAutocomplete() {
            if (!this.mapsReady || this.autocomplete || !this.$refs.placeSearch) return;
            const input = this.$refs.placeSearch.$refs.input;
            if (!input) return;
            const google = window.google;
            this.autocomplete = new google.maps.places.Autocomplete(input, {
                componentRestrictions: { country: "br" },
                fields: ["address_components", "geometry", "name"],
            });
            this.autocomplete.addListener("place_changed", () => {
                const place = this.autocomplete.getPlace();
                if (!place || !place.geometry) return this.warn("Escolha uma das sugestões da lista.");
                this.fillAddress(addressParts(place), true);
                this.placeQuery = "";
                input.value = "";
            });
        },
        fillAddress(parts, overwrite) {
            const set = (key, value) => {
                if (value === null || value === undefined || value === "") return;
                if (overwrite || !String(this.form[key] || "").trim()) this.form[key] = value;
            };
            set("address", parts.address);
            set("city", parts.city);
            set("state", parts.state);
            set("zip_code", parts.zip_code);
            if (parts.latitude !== null && (overwrite || !this.hasCoordinates)) {
                this.form.latitude = coordinateText(parts.latitude);
                this.form.longitude = coordinateText(parts.longitude);
            }
        },
        // CEP digitado: completa so os campos vazios.
        lookupZip() {
            const digits = String(this.form.zip_code || "").replace(/\D/g, "");
            if (digits.length !== 8 || !this.mapsReady) return;
            if (String(this.form.city || "").trim() && this.hasCoordinates) return;
            this.zipLoading = true;
            new window.google.maps.Geocoder().geocode({ componentRestrictions: { country: "BR", postalCode: digits } }, (results, status) => {
                this.zipLoading = false;
                if (status !== "OK" || !results || !results.length) return this.warn("CEP não encontrado. Confira ou preencha o endereço à mão.");
                this.fillAddress(addressParts(results[0]), false);
            });
        },
        onMapClick(event) {
            if (!event || !event.latLng) return;
            this.form.latitude = coordinateText(event.latLng.lat());
            this.form.longitude = coordinateText(event.latLng.lng());
        },
        clearCoordinates() {
            this.form.latitude = "";
            this.form.longitude = "";
        },
        async load() {
            this.appraisal = { file: null, saving: false, progress: 0 };
            if (this.isNew) {
                this.asset = null;
                this.media = [];
                this.form = { ...EMPTY };
                return;
            }
            this.loading = true;
            try {
                const result = await axios.get(`${API}/assets/${this.id}`);
                this.fill(result.data.content);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        fill(asset) {
            this.asset = asset;
            this.media = asset.media || [];
            this.form = {
                code: asset.code,
                asset_type: asset.asset_type,
                title: asset.title,
                description: asset.description || "",
                registration: asset.registration || "",
                registry_office: asset.registry_office || "",
                zip_code: asset.zip_code || "",
                address: asset.address || "",
                city: asset.city || "",
                state: asset.state,
                latitude: coordinateText(asset.latitude),
                longitude: coordinateText(asset.longitude),
                area: moneyInput(asset.area),
                area_unit: asset.area_unit,
                appraisal_value: moneyInput(asset.appraisal_value),
                appraisal_date: asset.appraisal_date ? String(asset.appraisal_date).slice(0, 10) : "",
                status: asset.status,
                block_reason: asset.block_reason || "",
                notes: asset.notes || "",
            };
        },
        async save() {
            if (this.missing.length) {
                this.showErrors = true;
                return this.warn(`Falta preencher: ${this.missing.join(", ")}.`);
            }
            this.saving = true;
            try {
                const payload = { ...this.form, block_reason: this.form.status === "available" ? "" : this.form.block_reason };
                if (this.isNew) {
                    const result = await axios.post(`${API}/processes/${this.processId}/assets`, payload);
                    notifySuccess(this, "Ativo criado. Agora envie o laudo e as fotos.");
                    this.$router.replace({ path: `/processo-competitivo/ativos/${result.data.content.id}` });
                    return;
                }
                const result = await axios.patch(`${API}/assets/${this.asset.id}`, payload);
                this.fill(result.data.content);
                notifySuccess(this, "Ativo salvo.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.saving = false;
            }
        },
        async remove() {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir ativo",
                text: `${this.asset.code} sai do processo e das cestas, lotes e blocos. Ativo já ofertado em etapa não pode ser excluído: bloqueie ou marque como retirado. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                const route = this.listRoute;
                await axios.delete(`${API}/assets/${this.asset.id}`);
                notifySuccess(this, "Ativo excluído.");
                this.$router.push(route);
            } catch (error) {
                notifyError(this, error);
            }
        },
        async saveAppraisal() {
            if (!this.appraisal.file) return;
            this.appraisal.saving = true;
            this.appraisal.progress = 0;
            try {
                const file = await uploadForRecord(this.appraisal.file, (percent) => { this.appraisal.progress = percent; });
                const result = await axios.put(`${API}/assets/${this.asset.id}/appraisal-file`, file);
                this.fill(result.data.content);
                this.appraisal.file = null;
                notifySuccess(this, "Laudo enviado.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.appraisal.saving = false;
            }
        },
        async downloadAppraisal() {
            try {
                const result = await axios.get(`${API}/assets/${this.asset.id}/appraisal-file`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
        async removeAppraisal() {
            try {
                const result = await axios.delete(`${API}/assets/${this.asset.id}/appraisal-file`);
                this.fill(result.data.content);
            } catch (error) {
                notifyError(this, error);
            }
        },
        async addMedia(event) {
            const files = [...(event.target.files || [])];
            event.target.value = "";
            if (!files.length) return;
            const tooBig = files.find((file) => file.size > 20 * 1024 * 1024);
            if (tooBig) return this.warn(`${tooBig.name} passa de 20 MB.`);
            this.mediaUpload = { busy: true, done: 0, total: files.length };
            try {
                for (const file of files) {
                    const uploaded = await uploadForRecord(file);
                    const result = await axios.post(`${API}/assets/${this.asset.id}/media`, uploaded);
                    this.media = result.data.content;
                    this.mediaUpload.done += 1;
                }
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.mediaUpload.busy = false;
            }
        },
        async setCover(item) {
            try {
                const result = await axios.patch(`${API}/media/${item.id}`, { is_cover: true });
                this.media = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },
        async removeMedia(item) {
            try {
                const result = await axios.delete(`${API}/media/${item.id}`);
                this.media = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";

.cp-asset-map {
    height: 320px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: #f4f4f5;
}
.cp-input-num input {
    text-align: right;
    font-variant-numeric: tabular-nums;
}
// Lista de sugestoes do Google acima dos menus do Vuetify.
.pac-container {
    z-index: 3000;
}
</style>
